// Shared types + formatting for the Performance page. Mirrors the shape
// returned by /backend/performance/getPerformance.ts.

export type Period = 'day' | 'week' | 'month' | 'total'

export type Kpis = {
  uber_gross: number | null
  driver_net: number | null
  petrol_cost: number | null
  petrol_budget: number | null
  fuel_saving: number | null
  driver_extras: number | null
  shifts: number
  active_drivers: number
  // Cancelled-excluded basis (see DAILY_GROSS_TARGET_ZAR note below).
  earnings_per_shift: number | null
  shifts_excl_cancelled: number
  avg_online_hours: number | null
  acceptance_rate: number | null
  avg_fare: number | null
  shifts_with_activity: number
}

export type TrendPoint = {
  bucket: string
  driver_net: number | null
  uber_gross: number | null
  petrol_cost: number | null
  // Per-bucket earnings per shift (cancelled excluded) and its shift count.
  earnings_per_shift: number | null
  shifts: number
}

export type ManagerSlice = {
  manager: string
  driver_net: number | null
}

export type DriverRow = {
  driver_id: string
  driver: string
  manager: string
  shifts: number
  uber_gross: number | null
  driver_net: number | null
  petrol_cost: number | null
  fuel_saving: number | null
  driver_extras: number | null
  // Cancelled-excluded basis.
  earnings_per_shift: number | null
  shifts_excl_cancelled: number
  avg_online_hours: number | null
  acceptance_rate: number | null
  avg_fare: number | null
  shifts_with_activity: number
}

export type VehicleRow = {
  vehicle: string
  model: string
  shifts: number
  kms: number
  uber_gross: number | null
  driver_net: number | null
  petrol_cost: number | null
  fuel_saving: number | null
  // Cancelled-excluded basis.
  earnings_per_shift: number | null
  shifts_excl_cancelled: number
  avg_online_hours: number | null
  acceptance_rate: number | null
  avg_fare: number | null
  shifts_with_activity: number
}

// One row of the shift-level drill-down, mirroring
// /backend/performance/getShiftPerformance.ts.
export type ShiftRow = {
  shift_id: string
  shift_date: string
  shift_window: string
  shift_status: string
  driver: string
  vehicle: string
  vehicle_model: string
  manager: string
  uber_earnings_zar: number
  petrol_cost_zar: number
  kilometres: number
  earnings_per_km_zar: number | null
  driver_earnings_zar: number
}

export type ShiftPerformanceData = {
  rows: ShiftRow[]
  total: number
}

// Daily gross target used to contextualise earnings per shift. Mirrors
// commission_rules.daily_gross_target_zar; should eventually be read from that
// table rather than hard-coded here.
// NOTE: live targets now come from the backend `targets` object (see the
// Targets type below). This constant is kept for back-compat as a fallback
// only, used when `targets` is null.
export const DAILY_GROSS_TARGET_ZAR = 1200

// One metric's target band, mirroring the shape returned by
// public.v5_performance_targets_v1(). Numeric fields are optional because they
// vary by metric; `unit`, `direction` and `source` are always present.
export type TargetBand = {
  minimum?: number; target?: number; recognition?: number; ceiling?: number
  // Average fare is information only since db/migrations/5814: no target, never coloured.
  informational?: boolean
  standard?: number; tolerance?: number; fleet_median?: number
  day_expected?: number; night_expected?: number; vehicle_pair_per_day?: number
  unit: string; direction: 'higher_better' | 'lower_better'; source: string
}

export type Targets = {
  earnings_per_shift: TargetBand; online_hours: TargetBand
  acceptance_rate: TargetBand; avg_fare: TargetBand
  utilisation_pct: TargetBand; trips_per_online_hour: TargetBand
  fuel_l_per_100km: TargetBand
  earnings_model?: EarningsModel
}

// Online hours a shift needs for the shift target at each acceptance level, in steps of five
// points. Computed by public.performance_hours_for_earnings_v1 (db/migrations/5814); the page
// only looks a value up, it never recomputes the rate.
export type EarningsModel = {
  base_per_hour: number; per_acceptance: number; shift_target: number
  hours_by_acceptance: { acceptance: number; hours: number }[]
  unit: string; direction: 'higher_better' | 'lower_better'; source: string
}

// The tabulated hours for the highest acceptance step at or below the measured acceptance.
export function hoursForShiftTarget(
  model: EarningsModel | undefined,
  acceptance: number | null,
): number | null {
  if (!model || acceptance === null || !Array.isArray(model.hours_by_acceptance)) return null
  let hit: number | null = null
  for (const row of model.hours_by_acceptance) {
    if (row.acceptance <= acceptance + 1e-9) hit = row.hours
  }
  return hit
}

export type PerformanceData = {
  kpis: Kpis
  trend: TrendPoint[]
  managers: ManagerSlice[]
  drivers: DriverRow[]
  vehicles: VehicleRow[]
  targets: Targets | null
}

export type PerformanceFilters = {
  from: string | null
  to: string | null
  manager: string
  vehicle: string
  period: Period
}

const randFmt = new Intl.NumberFormat('en-ZA', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
})

// Money as Rand with thousands separators, e.g. "R 1 234 567". Negatives as
// "-R 1 234". A missing figure is the no-value marker, never "R 0" (B16).
export function formatRand(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return '\u2014'
  const rounded = Math.round(value)
  const sign = rounded < 0 ? '-' : ''
  return `${sign}R ${randFmt.format(Math.abs(rounded))}`
}

// Compact Rand for chart axes, e.g. "R 1.2m", "R 340k".
export function formatRandCompact(value: number): string {
  const abs = Math.abs(value)
  const sign = value < 0 ? '-' : ''
  if (abs >= 1_000_000) return `${sign}R ${(abs / 1_000_000).toFixed(1)}m`
  if (abs >= 1_000) return `${sign}R ${Math.round(abs / 1_000)}k`
  return `${sign}R ${Math.round(abs)}`
}

const intFmt = new Intl.NumberFormat('en-ZA')

export function formatInt(value: number): string {
  return intFmt.format(Math.round(value))
}

const EM_DASH = '\u2014'

// Online hours to one decimal, e.g. "9.4 h". Em dash when not measured.
export function formatHours(v: number | null): string {
  if (v === null) return EM_DASH
  return `${v.toFixed(1)} h`
}

// A 0-1 fraction as a whole percent, e.g. 0.38 -> "38%". Em dash when not
// measured. Never renders "0%" for a missing measurement (that is null).
export function formatPct(v: number | null): string {
  if (v === null) return EM_DASH
  return `${Math.round(v * 100)}%`
}

// Exact Rand with no thousands scaling, e.g. "R 62". Em dash when not measured.
export function formatRandExact(v: number | null): string {
  if (v === null) return EM_DASH
  return `R ${Math.round(v)}`
}

// Shared verdict so colouring is identical across cards and tables.
export type Verdict = 'good' | 'warn' | 'bad' | 'unknown'

export function verdict(
  value: number | null,
  target: number | undefined,
  direction: 'higher_better' | 'lower_better',
): Verdict {
  if (value === null || target === undefined || target === null) return 'unknown'
  if (direction === 'higher_better') {
    if (value >= target) return 'good'
    if (value >= target * 0.9) return 'warn'
    return 'bad'
  }
  // lower_better
  if (value <= target) return 'good'
  if (value <= target * 1.1) return 'warn'
  return 'bad'
}

export function verdictClass(v: Verdict): string {
  switch (v) {
    case 'good':
      return 'text-emerald-600 dark:text-emerald-400'
    case 'warn':
      return 'text-amber-600 dark:text-amber-400'
    case 'bad':
      return 'text-red-600 dark:text-red-400'
    default:
      return 'text-muted-foreground'
  }
}

export function fullName(row: DriverRow): string {
  return row.driver
}
